import { useEffect, useState } from "react";
import {
  fetchPrograms,
  fetchTestimonials,
  fetchBlog,
  fetchFaqs,
} from "@/lib/api";
import SEO from "@/components/SEO";
import Hero from "@/components/home/Hero";
import TrustStrip from "@/components/home/TrustStrip";
import VideoShowcase from "@/components/home/VideoShowcase";
import ProgramsSection from "@/components/home/ProgramsSection";
import AboutFounder from "@/components/home/AboutFounder";
import TestimonialsSection from "@/components/home/TestimonialsSection";
import BlogStrip from "@/components/home/BlogStrip";
import FAQSection from "@/components/home/FAQSection";
import LeadFormSection from "@/components/home/LeadFormSection";
import { SITE_URL } from "@/lib/site";

const TESTIMONIAL_INTERVAL_MS = 5500;

const HOME_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${SITE_URL}/#business`,
  name: "Weight Loss Gurukul",
  alternateName: "Best Yoga for Weight Loss at Home — 1-on-1 Online Coaching",
  description:
    "India's best 1-on-1 online yoga coaching for weight loss — Yogasana, Pranayama, Meditation, Nutrition and Lifelong Habit Mastery. Trained in the T. Krishnamacharya lineage. Transform at home — no gym, no equipment, no commute.",
  image: `${SITE_URL}/logo192.png`,
  url: `${SITE_URL}/`,
  telephone: "+91 98864 73926",
  email: "hello@weightlossgurukul.com",
  priceRange: "₹₹",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Unit 101, Oxford Tower, 139 Old Airport Road, Kodihalli",
    addressLocality: "Bangalore",
    addressRegion: "Karnataka",
    postalCode: "560008",
    addressCountry: "IN",
  },
  founder: { "@type": "Person", name: "Dhruva Lingappa" },
  areaServed: { "@type": "Place", name: "Worldwide" },
  sameAs: [],
};

export default function Home() {
  const [programs, setPrograms] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [blog, setBlog] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  useEffect(() => {
    Promise.all([fetchPrograms(), fetchTestimonials(), fetchBlog(), fetchFaqs()])
      .then(([p, t, b, f]) => {
        setPrograms(p);
        setTestimonials(t);
        setBlog(b);
        setFaqs(f);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!testimonials.length) return;
    const id = setInterval(
      () => setActiveTestimonial((i) => (i + 1) % testimonials.length),
      TESTIMONIAL_INTERVAL_MS
    );
    return () => clearInterval(id);
  }, [testimonials.length]);

  const faqJsonLd = faqs.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  return (
    <div data-testid="home-page">
      <SEO
        title="Best Yoga for Weight Loss at Home — 1-on-1 Online Coaching | Weight Loss Gurukul"
        description="Lose weight at home with India's best 1-on-1 online yoga, pranayama and nutrition coaching from Dhruva Lingappa. Ancient Wisdom · Real Transformation. Trained in the T. Krishnamacharya lineage. Free 7-day starter consultation."
        path="/"
        keywords="best yoga for weight loss, online yoga for weight loss, yoga to lose weight at home, weight loss yoga online classes, 1 on 1 yoga coach, pranayama for weight loss, Vinyasa Krama yoga, yoga nutrition program, Dhruva Lingappa, Weight Loss Gurukul, Krishnamacharya tradition, transformation coach Bangalore"
        jsonLd={faqJsonLd || HOME_JSON_LD}
      />
      <Hero />
      <TrustStrip />
      <VideoShowcase />
      <ProgramsSection programs={programs} />
      <AboutFounder />
      <TestimonialsSection
        testimonials={testimonials}
        active={activeTestimonial}
        setActive={setActiveTestimonial}
      />
      <BlogStrip blog={blog} />
      <FAQSection faqs={faqs} />
      <LeadFormSection />
    </div>
  );
}
